import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	BarChart,
	Bar,
	XAxis,
	YAxis,
	CartesianGrid,
	Tooltip,
	Legend,
	ResponsiveContainer,
} from "recharts";
import type { HistoricalVelocityData } from "@/hooks/dashboard/useDashboardKPIs";
import { KPITooltip } from "./KPITooltip";

interface VelocityChartProps {
	data: HistoricalVelocityData[];
	tooltip?: string;
}

export function VelocityChart({ data, tooltip }: VelocityChartProps) {
	const { t } = useTranslation();

	const chartData = data.map(d => ({
		...d,
		label: formatPeriod(d.period),
	}));

	const isEmpty = data.length === 0 || data.every(d => d.matches === 0 && d.interviews === 0 && d.hires === 0);

	return (
		<Card>
			<CardHeader className={isEmpty ? "pb-2" : undefined}>
				<div className="flex items-center gap-2">
					<CardTitle className="text-lg">{t("dashboard.kpi.hiringVelocity", "Historical Pipeline Velocity")}</CardTitle>
					{tooltip && <KPITooltip text={tooltip} />}
				</div>
			</CardHeader>
			<CardContent>
				{isEmpty ? (
					<div className="text-muted-foreground text-sm py-2">
						{t("dashboard.kpi.noData")}
					</div>
				) : (
					<div className="h-[300px]">
						<ResponsiveContainer width="100%" height="100%">
							<BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
								<CartesianGrid strokeDasharray="3 3" opacity={0.1} />
								<XAxis dataKey="label" tick={{ fontSize: 11 }} />
								<YAxis allowDecimals={false} />
								<Tooltip
									contentStyle={{
										backgroundColor: "rgba(255, 255, 255, 0.95)",
										borderRadius: "0.5rem",
										border: "none",
										boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
									}}
								/>
								<Legend />
								<Bar
									dataKey="matches"
									name={t("dashboard.kpi.matchesCreatedLabel", "Matches")}
									fill="#6366f1"
									radius={[3, 3, 0, 0]}
								/>
								<Bar
									dataKey="interviews"
									name={t("dashboard.kpi.stages.interview", "Interviews")}
									fill="#14b8a6"
									radius={[3, 3, 0, 0]}
								/>
								<Bar
									dataKey="hires"
									name={t("dashboard.kpi.hires", "Hires")}
									fill="#22c55e"
									radius={[3, 3, 0, 0]}
								/>
							</BarChart>
						</ResponsiveContainer>
					</div>
				)}
			</CardContent>
		</Card>
	);
}

function formatPeriod(period: string): string {
	// Weekly format: "2026-W08" → "W8 '26"
	if (period.includes("-W")) {
		const [year, week] = period.split("-W");
		return `W${parseInt(week, 10)} '${year.slice(2)}`;
	}
	// Monthly format: "2026-02" → "Feb 2026"
	const [year, month] = period.split("-");
	const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
	return `${months[parseInt(month, 10) - 1]} ${year}`;
}
